"use client"

import { useState } from "react"
import { createRoute } from "@tanstack/react-router"
import { Plus } from "lucide-react"

import { CalendarEventDialog } from "@/components/calendar/calendar-event-dialog"
import {
  CalendarViewSwitch,
  GraphiteCalendar,
} from "@/components/calendar/graphite-calendar"
import { PageAction, PageHeader } from "@/components/graphite/page-header"
import { useCalendar } from "@/hooks/use-calendar"
import {
  createCalendarEventFormDefaults,
  mapEventToCalendarEventFormDefaults,
} from "@/lib/calendar-event-defaults"
import { periodLabel } from "@/lib/calendar-grid"
import { nextEndTime } from "@/hooks/use-event-form-logic"
import MainAppRoute from "@/rootRoutes/MainApp"
import type { Event } from "openapi/api/model"

/**
 * The clock pair a grid hour opens with.
 *
 * The end comes from `nextEndTime`, the same rule the composer reapplies while
 * the form is open, so the value seeded here and the value the form settles on
 * agree — the last row of the grid is the case where they used to differ. The
 * end is only omitted when there is no same-day end to give, which the form
 * defaults then cover.
 */
function hourDefaults(hour: number) {
  const start = `${String(hour).padStart(2, "0")}:00`
  const end = nextEndTime(start)
  return end ? { start_time: start, end_time: end } : { start_time: start }
}

const CalendarRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/calendar",
  component: CalendarPage,
})
export default CalendarRoute

/**
 * The calendar — canvas 33b and 33c, plus the Day the switcher has always
 * offered.
 *
 * 33b and 33c draw Month and Week and both show a Month / Week / Day switcher;
 * no artboard draws Day. It is built from the week's own grid rather than
 * invented: a day is a week of one column, so the hours, the lane packing and
 * the event blocks are all the same code.
 */
function CalendarPage() {
  const calendar = useCalendar()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create")
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
  /*
    The hour a click landed on, which the week and day grids know and the month
    grid does not. Without it the composer kept its 09:00 default, so clicking
    the 3 PM row offered a nine o'clock event.
  */
  const [selectedHour, setSelectedHour] = useState<number | null>(null)

  const openCreate = (date: Date, hour?: number) => {
    setSelectedHour(hour ?? null)
    setSelectedDate(date)
    setSelectedEvent(null)
    setDialogMode("create")
    setDialogOpen(true)
  }

  const openEvent = (event: Event) => {
    setSelectedEvent(event)
    setDialogMode("edit")
    setDialogOpen(true)
  }

  const label = periodLabel(calendar.cursor, calendar.view)
  const { receipts } = calendar

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <PageHeader
        title={label}
        description="Interviews, screenings and everything else the desk has booked"
        actions={
          <div className="flex items-center gap-2.5">
            <CalendarViewSwitch view={calendar.view} onChange={calendar.setView} />
            <PageAction tone="primary" onClick={() => openCreate(new Date())}>
              <Plus className="size-[15px]" />
              New Event
            </PageAction>
          </div>
        }
      >
        {/*
          The receipts line. Absent until the events have been read — every
          figure comes from an empty array until then, so it would otherwise
          state a confident row of zeroes about a calendar nobody has seen.

          33b counts "1 cancelled" here too. An event carries no status in the
          contract, so there is nothing to count and the figure is left out
          rather than filled with a zero that would read as "none cancelled".
        */}
        {!calendar.isLoading && !calendar.isError && (
          <p className="mt-2.5 font-mono text-[11.5px] leading-none text-muted-foreground">
            {receipts.total} event{receipts.total === 1 ? "" : "s"} · {receipts.interviews}{" "}
            interview{receipts.interviews === 1 ? "" : "s"} · {receipts.screenings} screening
            {receipts.screenings === 1 ? "" : "s"} · {receipts.today} today
          </p>
        )}
      </PageHeader>

      <GraphiteCalendar
        view={calendar.view}
        cursor={calendar.cursor}
        label={label}
        onStep={calendar.stepBy}
        onToday={calendar.goToday}
        events={calendar.events}
        hidden={calendar.hidden}
        onToggleType={calendar.toggleType}
        email={calendar.email}
        isLoading={calendar.isLoading}
        isError={calendar.isError}
        truncated={calendar.truncated}
        onOpenEvent={openEvent}
        onCreate={openCreate}
        /* "+N more" walks into that day rather than opening a popover over the
           grid — the day view is right there, and it holds every event. */
        onOpenDay={(day) => {
          calendar.setCursor(day)
          calendar.setView("day")
        }}
      />

      <CalendarEventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        selectedEvent={selectedEvent}
        defaultValues={
          selectedEvent
            ? mapEventToCalendarEventFormDefaults(selectedEvent)
            : selectedDate
              ? createCalendarEventFormDefaults({
                  date: selectedDate,
                  event_type: "interview",
                  ...(selectedHour === null ? {} : hourDefaults(selectedHour)),
                })
              : undefined
        }
      />
    </div>
  )
}
